import Constants from 'vtk.js/Sources/Widgets/Widgets3D/LineWidget/Constants';
import macro from 'vtk.js/Sources/macros';
import * as vtkMath from 'vtk.js/Sources/Common/Core/Math';

const { ShapeType } = Constants;

const MAX_POINTS = 3;

const handleGetters = ['getHandle1', 'getHandle2', 'getHandle3', 'getMoveHandle'];

export default function widgetBehavior(publicAPI, model) {
    model.classHierarchy.push('vtkCustomAngleWidget');
    model._isDragging = false;
    model._nPlacedHandles = 0;

    /**
     * Returns the handle at the specified index.
     * @param {number} handleIndex 0, 1, 2, or 3
     */
    publicAPI.getHandle = (handleIndex) =>
        model.widgetState[handleGetters[handleIndex]]();

    publicAPI.getHandleIndex = (handle) => {
        switch (handle) {
            case model.widgetState.getHandle1(): return 0;
            case model.widgetState.getHandle2(): return 1;
            case model.widgetState.getHandle3(): return 2;
            case model.widgetState.getMoveHandle(): return 3;
            default: return -1;
        }
    };

    publicAPI.isPlaced = () => model._nPlacedHandles >= MAX_POINTS;
    publicAPI.getPlacedHandlesCount = () => model._nPlacedHandles;

    // --------------------------------------------------------------------------
    // Interactor event
    // --------------------------------------------------------------------------

    function ignoreKey(e) {
        return e.altKey || e.controlKey || e.shiftKey;
    }

    function updateCursor(callData) {
        model._isDragging = true;
        const manipulator =
            model.activeState?.getManipulator?.() ?? model.manipulator;
        manipulator.handleEvent(callData, model._apiSpecificRenderWindow);
        model._apiSpecificRenderWindow.setCursor('grabbing');
        model._interactor.requestAnimation(publicAPI);
    }

    // --------------------------------------------------------------------------
    // Text methods
    // --------------------------------------------------------------------------

    publicAPI.setText = (text) => {
        model.widgetState.getText().setText(text);
        model._interactor.render();
    };

    // --------------------------------------------------------------------------
    // Handle positioning methods
    // --------------------------------------------------------------------------

    function getLineDirection(p1, p2) {
        const dir = vtkMath.subtract(p1, p2, []);
        vtkMath.normalize(dir);
        return dir;
    }

    function computeMousePosition(p1, callData) {
        const displayMousePos = publicAPI.computeWorldToDisplay(
            model._renderer,
            ...p1
        );
        const worldMousePos = publicAPI.computeDisplayToWorld(
            model._renderer,
            callData.position.x,
            callData.position.y,
            displayMousePos[2]
        );
        return worldMousePos;
    }

    /**
     * Updates the text origin to sit at the vertex (handle2) position.
     */
    function updateTextOrigin() {
        const vertexOrigin = model.widgetState.getHandle2().getOrigin();
        if (vertexOrigin) {
            model.widgetState.getText().setOrigin(vertexOrigin);
        }
    }

    /**
     * Orient handle representations.
     * handle1 (arm1, idx 0): orient toward vertex
     * handle2 (vertex, idx 1): orient toward arm1
     * handle3 (arm2, idx 2): orient toward vertex
     * moveHandle (idx 3): orient toward vertex if available
     */
    publicAPI.updateHandleOrientations = () => {
        const vertex = model.widgetState.getHandle2().getOrigin();
        const arm1 = model.widgetState.getHandle1().getOrigin();
        const arm2 = model.widgetState.getHandle3().getOrigin();
        const moveOrigin = model.widgetState.getMoveHandle().getOrigin();

        if (vertex && arm1) {
            const dir = getLineDirection(arm1, vertex);
            if (dir) {
                model.representations[0].setOrientation(dir); // arm1 (handle1)
                model.representations[1].setOrientation(dir); // vertex (handle2)
            }
        }
        if (vertex && arm2) {
            const dir = getLineDirection(arm2, vertex);
            if (dir) model.representations[2].setOrientation(dir); // arm2
        }
        if (vertex && moveOrigin) {
            const dir = getLineDirection(moveOrigin, vertex);
            if (dir) model.representations[3].setOrientation(dir); // moveHandle
        }
    };

    publicAPI.rotateHandlesToFaceCamera = () => {
        const viewMatrix = Array.from(model._camera.getViewMatrix());
        model.representations[0].setViewMatrix(viewMatrix);
        model.representations[1].setViewMatrix(viewMatrix);
        model.representations[2].setViewMatrix(viewMatrix);
    };

    publicAPI.updateHandleVisibility = (handleIndex) => {
        const handle = publicAPI.getHandle(handleIndex);
        const visibility = handle.getVisible() && handleIndex < model._nPlacedHandles;
        model.representations[handleIndex].setVisibilityFlagArray([
            visibility,
            visibility && handle.getShape() !== ShapeType.NONE,
        ]);
        model.representations[handleIndex].updateActorVisibility();
        model._interactor.render();
    };

    /**
     * Place handle at the given phase (click index 0/1/2).
     * Placement order: phase 0 = arm1 endpoint (handle1),
     *                  phase 1 = vertex/pivot   (handle2),
     *                  phase 2 = arm2 endpoint  (handle3).
     */
    publicAPI.placeHandle = (phase) => {
        const moveOrigin = model.widgetState.getMoveHandle().getOrigin();
        model._nPlacedHandles = phase + 1;

        if (phase === 0) {
            // Click 1: arm1 endpoint. Mirror vertex+arm2 so the PolyLine has a valid shape.
            publicAPI.getHandle(0).setOrigin(...moveOrigin); // handle1 = arm1
            publicAPI.getHandle(1).setOrigin(...moveOrigin); // handle2 = vertex (placeholder)
            publicAPI.getHandle(2).setOrigin(...moveOrigin); // handle3 = arm2 (placeholder)
            model.widgetState.getMoveHandle().setShape(publicAPI.getHandle(0).getShape());
        } else if (phase === 1) {
            // Click 2: vertex/pivot. Mirror arm2 to vertex as a starting point for phase 2.
            publicAPI.getHandle(1).setOrigin(...moveOrigin); // handle2 = vertex
            publicAPI.getHandle(2).setOrigin(...moveOrigin); // handle3 = arm2 (placeholder)
        } else if (phase === 2) {
            // Click 3: arm2 endpoint. All 3 points placed.
            publicAPI.getHandle(2).setOrigin(...moveOrigin); // handle3 = arm2
            publicAPI.loseFocus();
        }

        publicAPI.updateHandleOrientations();
        publicAPI.rotateHandlesToFaceCamera();
        updateTextOrigin();
    };

    // --------------------------------------------------------------------------
    // Left press: Place next handle or start drag
    // --------------------------------------------------------------------------

    publicAPI.handleLeftButtonPress = (e) => {
        if (
            !model.activeState ||
            !model.activeState.getActive() ||
            !model.pickable ||
            ignoreKey(e)
        ) {
            return macro.VOID;
        }

        if (model.activeState === model.widgetState.getMoveHandle() && model._nPlacedHandles === 0) {
            publicAPI.placeHandle(0); // vertex
        } else if (model.activeState === model.widgetState.getMoveHandle() && model._nPlacedHandles === 1) {
            publicAPI.placeHandle(1); // arm1
        } else if (model.activeState === model.widgetState.getMoveHandle() && model._nPlacedHandles === 2) {
            publicAPI.placeHandle(2); // arm2
            const callback = model.widgetState.getAddedMeasurementCallback();
            callback();
        } else if (model.dragable && !model.widgetState.getText().getActive()) {
            updateCursor(e);
        }

        publicAPI.invokeStartInteractionEvent();
        return macro.EVENT_ABORT;
    };

    // --------------------------------------------------------------------------
    // Mouse move: Drag selected handle / Handle follow the mouse
    // --------------------------------------------------------------------------

    publicAPI.handleMouseMove = (callData) => {
        const manipulator =
            model.activeState?.getManipulator?.() ?? model.manipulator;
        if (
            manipulator &&
            model.pickable &&
            model.dragable &&
            model.activeState &&
            model.activeState.getActive() &&
            !ignoreKey(callData)
        ) {
            const { worldCoords, worldDelta } = manipulator.handleEvent(
                callData,
                model._apiSpecificRenderWindow
            );

            const isHandleMoving =
                model.activeState === model.widgetState.getMoveHandle() ||
                model._isDragging;

            // PolyLine representation does not have setOrigin
            const isDraggingLine = !model.activeState.setOrigin;

            if (isHandleMoving) {
                if (!isDraggingLine) {
                    const curOrigin = model.activeState.getOrigin();
                    if (curOrigin) {
                        model.activeState.setOrigin(
                            vtkMath.add(model.activeState.getOrigin(), worldDelta, [])
                        );
                    } else {
                        model.activeState.setOrigin(worldCoords);
                    }
                } else {
                    // Dragging the whole angle: move all 3 placed handles
                    for (let i = 0; i < MAX_POINTS; i++) {
                        const handleOrigin = publicAPI.getHandle(i).getOrigin();
                        if (handleOrigin) {
                            publicAPI
                                .getHandle(i)
                                .setOrigin(vtkMath.add(handleOrigin, worldDelta, []));
                        }
                    }
                }
                // During placement, keep the next handle tracking moveHandle for live preview.
                // Phase 1 (arm1 placed): vertex (handle2) follows cursor so the first arm is visible.
                // Phase 2 (vertex placed): arm2 (handle3) follows cursor for live arc + angle.
                const liveOrigin = model.widgetState.getMoveHandle().getOrigin();
                if (liveOrigin) {
                    if (model._nPlacedHandles === 1) {
                        model.widgetState.getHandle2().setOrigin(liveOrigin);
                    } else if (model._nPlacedHandles === 2) {
                        model.widgetState.getHandle3().setOrigin(liveOrigin);
                    }
                }
                publicAPI.updateHandleOrientations();
                updateTextOrigin();
                publicAPI.invokeInteractionEvent();
                return macro.EVENT_ABORT;
            }
        }
        return macro.VOID;
    };

    // --------------------------------------------------------------------------
    // Left release: Finish drag
    // --------------------------------------------------------------------------

    publicAPI.handleLeftButtonRelease = () => {
        if (
            !model.activeState ||
            !model.activeState.getActive() ||
            !model.pickable
        ) {
            publicAPI.rotateHandlesToFaceCamera();
            return macro.VOID;
        }
        if (model.hasFocus && publicAPI.isPlaced()) {
            publicAPI.loseFocus();
            return macro.VOID;
        }

        if (model._isDragging && publicAPI.isPlaced()) {
            const wasTextActive = model.widgetState.getText().getActive();
            model.widgetState.deactivate();
            model.activeState = null;
            if (!wasTextActive) {
                model._interactor.cancelAnimation(publicAPI);
            }
            model._apiSpecificRenderWindow.setCursor('pointer');
            model.hasFocus = false;
            model._isDragging = false;
        } else if (model.activeState !== model.widgetState.getMoveHandle()) {
            model.widgetState.deactivate();
        }

        if (
            (model.hasFocus && !model.activeState) ||
            (model.activeState && !model.activeState.getActive())
        ) {
            model._widgetManager.enablePicking();
            model._interactor.render();
        }

        publicAPI.invokeEndInteractionEvent();
        return macro.EVENT_ABORT;
    };

    // --------------------------------------------------------------------------
    // Focus API - moveHandle follows mouse when widget has focus
    // --------------------------------------------------------------------------

    publicAPI.grabFocus = () => {
        if (!model.hasFocus && !publicAPI.isPlaced()) {
            model.activeState = model.widgetState.getMoveHandle();
            model.activeState.setShape(publicAPI.getHandle(0).getShape());
            model.activeState.activate();
            model._interactor.requestAnimation(publicAPI);
            publicAPI.invokeStartInteractionEvent();
        }
        model.hasFocus = true;
    };

    publicAPI.loseFocus = () => {
        if (model.hasFocus) {
            model._interactor.cancelAnimation(publicAPI);
            publicAPI.invokeEndInteractionEvent();
        }
        model.widgetState.deactivate();
        model.widgetState.getMoveHandle().deactivate();
        model.widgetState.getMoveHandle().setOrigin(null);
        model.activeState = null;
        model.hasFocus = false;
        model._widgetManager.enablePicking();
        model._interactor.render();
    };

    publicAPI.reset = () => {
        model.widgetState.deactivate();
        model.widgetState.getMoveHandle().deactivate();

        model.widgetState.getHandle1().setOrigin(null);
        model.widgetState.getHandle2().setOrigin(null);
        model.widgetState.getHandle3().setOrigin(null);
        model.widgetState.getMoveHandle().setOrigin(null);
        model.widgetState.getText().setOrigin(null);
        model.widgetState.getText().setText('');

        model._nPlacedHandles = 0;
        model.activeState = null;
    };
}
